import { ChangeDetectorRef, Component, OnDestroy, OnInit, Inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ScrollStrategy, ScrollStrategyOptions, OverlayModule } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { FormArray, FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from 'src/_environments/environment';
import { User } from 'src/_model/user';
import { Language, Sourcetype } from 'src/_model/shared';
import { AuthService } from 'src/_service/AuthService';
import { DrawerIdentifier, DrawerService } from 'src/_service/DrawerService';
import { GlobalService } from 'src/_service/GlobalService';
import { WINDOW } from 'src/_service/WindowService';
import { OrderService } from '../../_service/OrderService';
import { CustomerService } from '../../_service/CustomerService';
import { clickAnimation } from '../../_theme/animations';
import { CMSpageComponent } from '../cmspage/cmspage.component';


@Component({
    selector: 'topmenu',
    templateUrl: './topmenu.component.html',
    animations: [clickAnimation],
    imports: [RouterLink, NgClass, AsyncPipe, TranslateModule, NgTemplateOutlet, ReactiveFormsModule, OverlayModule, CMSpageComponent]
})

export class TopMenuComponent implements OnInit, OnDestroy {

  env = { ...environment };
  show_userbuttons = true;
  routeEventSubscription: any;
  currentUser: User = null;

  lastCartquantity: number;
  scrollStrategy: ScrollStrategy;

  is_in_cart = false;
  clickanimation = false;
  get orderlines(): FormArray {
    return this._orderService.orderlines as FormArray;
  }
  get order(): FormGroup {
    return this._orderService.order as FormGroup;
  }

  isAuthenticated = this._authService.isAuthenticated$;
  isInBrowser = this._globalService.isInBrowser;
  
  preferredlanguageForm = new FormControl('');
  languages: string[] = [];
  current_lang: Language;
  showLanguageOverlay = false;

  private _unsubscribe$ = new Subject<void>();

  constructor(
    private _authService: AuthService,
    public router: Router,
    private _orderService: OrderService,
    private drawerService: DrawerService,
    private readonly sso: ScrollStrategyOptions,
    private _cd: ChangeDetectorRef,
    private _globalService: GlobalService,
    private _translateService: TranslateService,
    private _customerService: CustomerService,
    @Inject(WINDOW) private window: Window,
  ) {
  }
 
  ngOnInit() {
    this.routeEventSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url = event['urlAfterRedirects'].split(/[\/\?]/);
        const url_start = url[1];
        
        if ((url_start === 'personalmenu') && (url[2] !== 'lifestyles')) {
          this.show_userbuttons = false;
        } else {
          this.show_userbuttons = true;
        }

        if (url_start === 'checkout' && url[2] !== 'webshop') {
          this.is_in_cart = true
        } else {
          this.is_in_cart = false;
        }
      }
    });
    
    this.scrollStrategy = this.sso.noop();
    // listen to this here to make sure we set current user after we have it.
    this.isAuthenticated.pipe(takeUntil(this._unsubscribe$)).subscribe(
      (data) => {
        if (data) {
          this.currentUser = this._authService.user();
        } else {
          this.currentUser = null;
        }
        this._cd.detectChanges();
      }
    )
    
    this.languages = this._globalService.getLanguagecodes();
    this.current_lang = Language[this._globalService.getLanguage().toUpperCase()];
    this.preferredlanguageForm.setValue(this.current_lang);
    
    this.preferredlanguageForm.valueChanges.pipe(takeUntil(this._unsubscribe$)).subscribe(value => {
      this._globalService.setLanguage(Sourcetype.CUSTOMER, Language[value]);
      
      if (this._authService.isAuthenticatedValue && this._customerService.customerForm.get('id').value) {
        this._customerService.customerForm.get('preferredlanguage').setValue(Language[value], { emitEvent: false });
        this._customerService.saveCustomer(this._customerService.customerForm, { preferredlanguage: Language[value] }).subscribe();
      }
      
      const tld = this._globalService.getTld();
      const trans_url = this.window.document.querySelector(`link[hreflang^=${value.toLowerCase()}-${tld}]`);
      const href = trans_url?.['href'];
      const path = href && !href.endsWith('/home') ? href : null;
      this.reloadPage(path);
    });
    
    this._translateService.onLangChange.pipe(takeUntil(this._unsubscribe$)).subscribe(val => {
      this.preferredlanguageForm.setValue(val.lang.toUpperCase(), { emitEvent: false });
    });
  }
  
  reloadPage(path: string | null) {
    if (this._globalService.isInBrowser) {
      if (!!path) {
        this.window.location.href = path;
      } else {
        this.window.location.reload();
      }
    }
  }

  ngOnDestroy() {
    this.routeEventSubscription.unsubscribe();
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  toggleDrawer() {
    this.drawerService.toggle(DrawerIdentifier.leftMenuDrawer);
  }

  orderlineTotalQuantity() {
    const qtys = this.orderlines?.value.map(l => l.quantity) as number[];
    let qty = qtys.reduce((a, b) => a + b, 0)

    if (qty != this.lastCartquantity) {
      this.lastCartquantity = qty;
      this.clickanimation = true;
      setTimeout(() => {
        this.clickanimation = false;
      },500)
    }
    return qty
  }

  removeOrderline(index: number) {
    this._orderService.removeOrderlineByIndex(index);
  }
  
  toggleLanguageOverlay() {
    this.showLanguageOverlay = !this.showLanguageOverlay;
  }
  
  selectLanguage(code: string) {
    this.preferredlanguageForm.setValue(code);
    this.showLanguageOverlay = false;
  }

}
